import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass';
import { LUTPass } from "three/examples/jsm/postprocessing/LUTPass";
import { PassFactory } from "../services/render.pass.service";
import AppData from "../../app.data";
import { Loader } from "../../loaders/assets/loader";
import { resolveRenderQualityCapsForQuality } from "../qualityCaps";

const appData = new AppData();

export class PostProcessingManager {
    composer: EffectComposer;
    renderTarget: THREE.WebGLRenderTarget | null = null;
    outlinePass: OutlinePass | null = null;
    bloomPass: any;
    lutPass: LUTPass | null = null;
    passFactory: PassFactory;
    loader: Loader;
    renderer: THREE.WebGLRenderer;
    canvasRect: DOMRect | null = null;
    addToRenderQueueCallback: (frames?: number) => void;

    constructor(renderer: THREE.WebGLRenderer, addToRenderQueueCallback: (frames?: number) => void) {
        this.renderer = renderer;
        this.passFactory = new PassFactory();
        this.loader = new Loader();
        this.addToRenderQueueCallback = addToRenderQueueCallback;
    }

    public init(canvasRect: DOMRect, isLQ: boolean) {
        this.canvasRect = canvasRect;
        var parameters = {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            stencilBuffer: isLQ ? false : true,
            type: isLQ ? THREE.UnsignedByteType : THREE.HalfFloatType,
            samples: isLQ ? 0 : 4
        };

        this.renderTarget = new THREE.WebGLRenderTarget(
            canvasRect.width,
            canvasRect.height,
            parameters
        );

        this.composer = new EffectComposer(this.renderer, this.renderTarget);
    }

    public dispose() {
        if (this.composer) {
            for (let index = this.composer.passes.length + 1; index > 0; index--) {
                this.composer.removePass(this.composer.passes[index]);
                this.composer.passes[index] = null;
            }
            this.composer.dispose();
            // @ts-ignore
            this.composer = null;
        }
        if (this.renderTarget) {
            this.renderTarget.dispose();
            this.renderTarget = null;
        }
        this.passFactory?.dispose();
    }

    private hasFxaaPass(): boolean {
        return Boolean(this.composer?.passes?.some((pass: any) => pass?.name === "fxaa"));
    }

    private withFxaaShaderDiagnosticsSuppressed(operation: () => void) {
        const debugState = (this.renderer as any)?.debug;
        if (!this.hasFxaaPass() || !debugState || typeof debugState.checkShaderErrors !== "boolean") {
            operation();
            return;
        }

        const previousCheckShaderErrors = debugState.checkShaderErrors;
        debugState.checkShaderErrors = false;
        try {
            operation();
        } finally {
            debugState.checkShaderErrors = previousCheckShaderErrors;
        }
    }

    public render() {
        this.withFxaaShaderDiagnosticsSuppressed(() => {
            this.composer?.render();
        });
    }

    public setSize(width: number, height: number) {
        this.composer?.setSize(width, height);
    }

    private resizeComposerForQuality(renderQuality: string) {
        if (!this.canvasRect) return;
        const scale = resolveRenderQualityCapsForQuality(renderQuality).postProcessingScale;
        const width = Math.max(1, Math.floor(this.canvasRect.width * scale));
        const height = Math.max(1, Math.floor(this.canvasRect.height * scale));
        this.composer.setSize(width, height);

        const fxaaPass: any = this.getPassByName("fxaa");
        if (fxaaPass && fxaaPass.uniforms && fxaaPass.uniforms['resolution']) {
            fxaaPass.uniforms['resolution'].value.x = 1 / width;
            fxaaPass.uniforms['resolution'].value.y = 1 / height;
        }
    }

    public setComposer(renderSettings: any, camera: THREE.Camera, renderQuality: string, debug: boolean) {
        // Skip post-processing setup in VR mode
        if (this.renderer.xr.isPresenting) {
            return;
        }

        // if (!renderSettings.postProcessing.enabled) return;
        let activeCamera = camera;
        if (debug) {
            activeCamera = appData.debugCamera;
        }

        if (!this.canvasRect) return;

        this.passFactory.createPasses(
            this.composer,
            renderSettings,
            activeCamera,
            renderQuality,
            this.canvasRect,
            (pass) => { this.outlinePass = pass; },
            (pass) => { this.bloomPass = pass; },
            (pass) => { this.lutPass = pass; }
        );

        this.resizeComposerForQuality(renderQuality);
    }

    public setOutlinedObject(object: THREE.Object3D | null) {
        if (!this.outlinePass) return;

        const result: THREE.Object3D[] = [];

        object?.traverse(child => {
            if (child instanceof THREE.Mesh && child.name !== 'bounding-cylinder' && child.name !== 'pedestal-collider') {
                result.push(child);
            }
        });

        this.outlinePass.selectedObjects = result ? result : [];
        this.addToRenderQueueCallback();
    }

    public updateLUT(lut: any) {
        if (!this.lutPass) {
            this.lutPass = new LUTPass({});
        }
        if (!lut?.cubeMapUrl) {
            this.lutPass.lut = null;
            this.lutPass.intensity = 0;
            this.addToRenderQueueCallback();
            return;
        }
        this.loader.loadLUTCube(lut.cubeMapUrl).then((lutFile: any) => {
            if (this.lutPass) {
                this.lutPass.lut = lutFile.texture3D;
                this.lutPass.intensity = lut.intensity;
                this.addToRenderQueueCallback();
            }
        });
    }

    private getPassByName(name: string) {
        // @ts-ignore
        return this.composer.passes.find(pass => pass.name === name);
    }

    public updateDotScreen(postProcessing: any) {
        const dotScreenPass = this.getPassByName("dotScreen");
        if (dotScreenPass) {
            // @ts-ignore
            dotScreenPass.uniforms.scale.value = postProcessing.scale;
            // @ts-ignore
            dotScreenPass.uniforms.angle.value = postProcessing.angle;
            this.addToRenderQueueCallback();
        }
    }

    public updateNoise(postProcessing: any) {
        const noisePass = this.getPassByName("noise");
        if (noisePass) {
            const intensity = postProcessing.amount ?? postProcessing.threshold ?? 1;
            // @ts-ignore
            noisePass.uniforms.intensity.value = intensity;
            this.addToRenderQueueCallback();
        }
    }

    /**
     * Sync post-processing effects with mood settings.
     * Called when mood changes to ensure renderer effects match mood data.
     */
    public syncWithMood(postProcessing: any[]) {
        if (!postProcessing || !Array.isArray(postProcessing)) return;

        // Update bloom pass
        const bloomSettings = postProcessing.find((p: any) => p.type === 'bloom');
        if (bloomSettings && this.bloomPass) {
            this.bloomPass.threshold = bloomSettings.threshold ?? 0.75;
            this.bloomPass.strength = bloomSettings.strength ?? 1.0;
            this.bloomPass.radius = bloomSettings.radius ?? 0.5;
        }

        // Update LUT pass
        const lutSettings = postProcessing.find((p: any) => p.type === 'lut');
        if (lutSettings && this.lutPass) {
            this.lutPass.intensity = lutSettings.intensity ?? 1.0;
            // Note: LUT texture itself is loaded separately via updateLUT
        }

        // Update dot screen pass
        const dotScreenSettings = postProcessing.find((p: any) => p.type === 'dotScreen');
        if (dotScreenSettings) {
            this.updateDotScreen(dotScreenSettings);
        }

        // Update noise pass
        const noiseSettings = postProcessing.find((p: any) => p.type === 'noise');
        if (noiseSettings) {
            this.updateNoise(noiseSettings);
        }

        this.addToRenderQueueCallback();
    }

    public async updateHDR(hdri: any) {
        if (hdri && hdri.length > 3) {
            try {
                const hdriFile = await this.loader.loadHDR(hdri, this.renderer);
                appData.spaceScene.environment = hdriFile;
                appData.artworksScene.environment = hdriFile;
                this.addToRenderQueueCallback();
                return true;
            } catch (error) {
                console.log("failed to load hdri file");
                return false;
            }
        } else {
            console.log("invalid hdri file provided");
            return false;
        }
    }

    public async updateSkyImage(skyImage: any, skyModelName: string) {
        if (skyImage && skyImage.length > 3 && skyModelName) {
            const skyModel = appData.spaceScene.getObjectByName(skyModelName);
            if (skyModel) {
                try {
                    const skyImageFile = await this.loader.loadTexture(skyImage);
                    // @ts-ignore
                    skyModel.material.map = skyImageFile;
                    // @ts-ignore
                    skyModel.material.needsUpdate = true;
                    this.addToRenderQueueCallback();
                    return true;
                } catch (error) {
                    console.log("failed to load sky image file", error);
                    return false;
                }
            }
        } else {
            console.log("invalid sky image data");
            return false;
        }
    }
}

